// All bubble-field tuning knobs in one file. Group + comment so
// future-you can iterate without hunting through three modules.

// Depth layers: each layer is an independent collision group with its
// own depth range and population. Bubbles only pair-separate against
// other bubbles in the same layer (matches the 3D mental model: two
// objects at very different depths shouldn't physically collide).
// Far layers are denser → background haze; near layers are sparser →
// the few sharp foreground bubbles.
export type DepthLayer = {
  depthMin: number;
  depthMax: number;
  count: number;
};

export const DEPTH_LAYERS: DepthLayer[] = [
  { depthMin: 0.0, depthMax: 0.1, count: 132 },
  { depthMin: 0.1, depthMax: 0.25, count: 36 },
  { depthMin: 0.25, depthMax: 0.5, count: 30 },
  { depthMin: 0.5, depthMax: 0.75, count: 40 },
  { depthMin: 0.75, depthMax: 1.0, count: 20 },
];

export const BUBBLE_COUNT = DEPTH_LAYERS.reduce((s, l) => s + l.count, 0);
export const BUBBLE_BASE_SIZE = 48; // px at depth = 1

// Depth → derived properties (linear lerp across [0, 1]).
export const SIZE_FAR_FACTOR = 0.45;
export const SIZE_NEAR_FACTOR = 1.3;
export const OPACITY_FAR = 0.32;
export const OPACITY_NEAR = 1.0;
export const BLUR_FAR_PX = 6;
export const BLUR_SHARP_FROM = 0.8; // depth ≥ this → 0 blur (top 20% sharp)
export const SPEED_FAR_FACTOR = 0.35;
export const SPEED_NEAR_FACTOR = 1.1;

// Curl-noise flow (the engine of motion).
export const NOISE_SCALE = 0.0015; // larger = swirly, smaller = laminar
export const NOISE_TIME_SCALE = 0.00015; // how fast the field evolves
export const FLOW_FORCE = 0.04;

// Speed envelope (base; per-bubble multiplier comes from depth).
export const MAX_SPEED = 0.6; // px/frame — calm
export const VEL_DAMP = 0.995; // hygiene damping; not the source of stop

// UI avoidance (lateral steer with lookahead — never radial).
export const LOOKAHEAD = 30; // frames of velocity to project
export const UI_BUFFER = 28; // px outside the rect where steer kicks in
export const UI_AVOID_FORCE = 0.25; // > FLOW so it dominates near walls

// Depth scaling for UI avoidance. Foreground bubbles render at
// z-index 11+ (above HomeContent at z-10) so any anchor overlap is
// fully visible — they need stricter avoidance. Background bubbles
// sit behind the UI and can drift through without visual harm.
// Effective force/buffer = UI_AVOID_FORCE × (MIN + (MAX-MIN) × depth).
export const UI_AVOID_DEPTH_MIN = 0.5;
export const UI_AVOID_DEPTH_MAX = 2.0;

// Cursor flee (must beat UI avoid so there are no dead zones).
export const CURSOR_RADIUS = 180;
export const CURSOR_FORCE = 0.45;

// Pair separation (short-range only; long-range stalls motion).
// Bubbles only repel within the same depth layer.
export const SEP_RANGE_FACTOR = 1.4; // × sum-of-radii
export const SEP_FORCE = 0.3;

// Gentle tangential bias around the cluster centroid → "in orbit"
// without ever pulling bubbles into the rects (it's perpendicular).
export const ORBIT_FORCE = 0.008;
